import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, AlertCircle, Info, X } from 'lucide-react';

const Toast = ({ message, type = 'success', onClose, duration = 3000 }) => {
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  const config = {
    success: {
      icon: CheckCircle,
      bgColor: '#10b981',
      textColor: '#ffffff',
    },
    error: {
      icon: XCircle,
      bgColor: '#ef4444',
      textColor: '#ffffff',
    },
    warning: {
      icon: AlertCircle,
      bgColor: '#f59e0b',
      textColor: '#ffffff',
    },
    info: {
      icon: Info,
      bgColor: '#3b82f6',
      textColor: '#ffffff',
    },
  };

  const { icon: Icon, bgColor, textColor } = config[type] || config.success;

  const styles = {
    container: {
      position: 'fixed',
      top: '2rem',
      right: '2rem',
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      padding: '1rem 1.5rem',
      borderRadius: '12px',
      background: bgColor,
      color: textColor,
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.25)',
      minWidth: '300px',
      maxWidth: '500px',
    },
    content: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
    },
    message: {
      fontSize: '0.95rem',
      fontWeight: 500,
      lineHeight: 1.4,
    },
    closeButton: {
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      padding: '0.25rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: textColor,
      opacity: 0.8,
      transition: 'opacity 200ms',
    },
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -50, scale: 0.9 }}
      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
      style={styles.container}
    >
      <div style={styles.content}>
        <Icon size={24} />
        <div style={styles.message}>{message}</div>
      </div>
      <button
        onClick={onClose}
        style={styles.closeButton}
        onMouseEnter={(e) => (e.target.style.opacity = '1')}
        onMouseLeave={(e) => (e.target.style.opacity = '0.8')}
      >
        <X size={18} />
      </button>
    </motion.div>
  );
};

// Toast container component to manage multiple toasts
export const ToastContainer = ({ toasts, removeToast }) => {
  return (
    <AnimatePresence>
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </AnimatePresence>
  );
};

// Custom hook for using toasts
export const useToast = () => {
  const [toasts, setToasts] = React.useState([]);

  const addToast = React.useCallback((message, type = 'success', duration = 3000) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, type, duration }]);
  }, []);

  const removeToast = React.useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showSuccess = React.useCallback((message) => {
    addToast(message, 'success');
  }, [addToast]);

  const showError = React.useCallback((message) => {
    addToast(message, 'error');
  }, [addToast]);

  const showWarning = React.useCallback((message) => {
    addToast(message, 'warning');
  }, [addToast]);

  const showInfo = React.useCallback((message) => {
    addToast(message, 'info');
  }, [addToast]);

  return {
    toasts,
    removeToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };
};

export default Toast;